/**
 * 1.3.0 the portal's resumable uploads (the API's /api/v1/portal/uploads protocol: create, chunks at Upload-Offset,
 * complete). An upload survives a lost connection (it waits until the browser is back online and asks the server
 * for its offset) and a reload: the upload's id and the file's name, size and modified time are kept in
 * localStorage, the page asks for the same file again, and the upload continues from the server's offset.
 *
 * Uploads live in a module-level registry, so moving between the portal's pages keeps them running.
 */
import { PortalApiError, portalApi } from './client';

export interface UploadTarget { itemId?: string; submissionId?: string }
/** What localStorage keeps of an upload that has not finished. */
export interface PendingUpload { key: string; id: string; name: string; size: number; lastModified: number; target: UploadTarget; savedAt: number }

const STORE = 'prism-portal-uploads';

export function readPending(): PendingUpload[] {
  try {
    const v = JSON.parse(localStorage.getItem(STORE) ?? '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function writePending(list: PendingUpload[]): void {
  try { localStorage.setItem(STORE, JSON.stringify(list)); } catch { /* private mode: resume after reload is unavailable */ }
}
export function savePending(p: PendingUpload): void { writePending([...readPending().filter((x) => x.key !== p.key), p]); }
export function dropPending(key: string): void { writePending(readPending().filter((x) => x.key !== key)); }
export const pendingFor = (key: string) => readPending().find((p) => p.key === key) ?? null;
/** Whether a chosen file is the one an interrupted upload was for. */
export const sameFile = (p: Pick<PendingUpload, 'name' | 'size' | 'lastModified'>, f: File) => f.name === p.name && f.size === p.size && f.lastModified === p.lastModified;

export type UploadState = 'starting' | 'sending' | 'offline' | 'completing' | 'done' | 'failed' | 'cancelled';
export interface UploadResult { uploadId: string; submissionId: string; itemId: string | null; versionId: string | null; attachmentId: string | null; scan?: 'clean' | 'skipped' }

interface UploadDTO { id: string; receivedBytes: number; sizeBytes: number; status: string; chunkSize: number; submissionId?: string }

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const waitOnline = () => (navigator.onLine ? sleep(1500) : new Promise<void>((r) => window.addEventListener('online', () => r(), { once: true })));

/** One file's upload. Listeners get every change (progress, state, error). */
export class PortalUpload {
  state: UploadState = 'starting';
  sent = 0;
  /** Bytes per second, smoothed. */
  speed = 0;
  error: PortalApiError | Error | null = null;
  result: UploadResult | null = null;
  id: string | null;
  private xhr: XMLHttpRequest | null = null;
  private listeners = new Set<() => void>();
  private lastTick = { at: 0, bytes: 0 };

  readonly key: string;
  readonly file: File;
  readonly target: UploadTarget;

  constructor(key: string, file: File, target: UploadTarget, resumeId: string | null = null) {
    this.key = key;
    this.file = file;
    this.target = target;
    this.id = resumeId;
  }

  /** Read through a method, so TypeScript does not narrow it away: cancel() changes it while run() awaits. */
  private cancelled() { return (this.state as UploadState) === 'cancelled'; }

  get total() { return this.file.size; }
  get active() { return ['starting', 'sending', 'offline', 'completing'].includes(this.state); }
  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  private emit() { for (const l of this.listeners) l(); }
  private set(state: UploadState) { this.state = state; this.emit(); }

  async run(): Promise<UploadResult | null> {
    try {
      let chunk: number;
      if (!this.id) {
        const created = await portalApi<UploadDTO>('/uploads', { method: 'POST', json: { filename: this.file.name, sizeBytes: this.file.size, ...this.target } });
        this.id = created.id;
        chunk = created.chunkSize;
        savePending({ key: this.key, id: created.id, name: this.file.name, size: this.file.size, lastModified: this.file.lastModified, target: { ...this.target, ...(created.submissionId ? { submissionId: created.submissionId } : {}) }, savedAt: Date.now() });
      } else {
        const cur = await portalApi<UploadDTO>(`/uploads/${this.id}`);
        if (cur.status === 'complete') return this.finish();
        if (cur.status !== 'open') throw new PortalApiError(409, 'UPLOAD_CLOSED', 'This upload was closed; start it again');
        this.sent = cur.receivedBytes;
        chunk = cur.chunkSize;
      }
      this.set('sending');
      while (this.sent < this.file.size) {
        if (this.cancelled()) return null;
        try {
          this.sent = await this.sendChunk(this.sent, Math.min(this.file.size, this.sent + chunk));
          if (this.state === 'offline') this.set('sending');
        } catch (err) {
          if (this.cancelled()) return null;
          if (err instanceof PortalApiError && err.code === 'OFFSET_MISMATCH' && typeof err.details?.receivedBytes === 'number') {
            this.sent = err.details.receivedBytes;
            continue;
          }
          if (err instanceof PortalApiError && err.status !== 503 && err.status !== 0) throw err;
          // A lost connection or a busy server: wait, then ask where the server is.
          this.set('offline');
          await waitOnline();
          const cur = await this.offset();
          if (cur !== null) this.sent = cur;
        }
      }
      return await this.finish();
    } catch (err) {
      if (this.cancelled()) return null;
      this.error = err as Error;
      // A refused file (a virus, the checks) is closed on the server: nothing to resume.
      if (err instanceof PortalApiError && [404, 409, 413, 415, 422].includes(err.status)) dropPending(this.key);
      this.set('failed');
      return null;
    }
  }

  private async offset(): Promise<number | null> {
    for (;;) {
      try {
        return (await portalApi<UploadDTO>(`/uploads/${this.id}`)).receivedBytes;
      } catch (err) {
        if (err instanceof PortalApiError && err.status !== 503) throw err;
        await waitOnline();
      }
    }
  }

  private async finish(): Promise<UploadResult | null> {
    this.set('completing');
    for (let attempt = 0; ; attempt++) {
      try {
        this.result = await portalApi<UploadResult>(`/uploads/${this.id}/complete`, { method: 'POST', json: {} });
        dropPending(this.key);
        this.sent = this.file.size;
        this.set('done');
        return this.result;
      } catch (err) {
        // 503: the scanner or the storage is away for a moment, or another request still works on it; the upload is kept.
        const retry = !(err instanceof PortalApiError) || err.status === 503 || err.status === 0;
        if (!retry || attempt >= 20) throw err;
        await sleep(Math.min(15_000, 1000 * 2 ** Math.min(attempt, 4)));
      }
    }
  }

  /** One chunk with XMLHttpRequest, for progress within the chunk. Answers the server's new offset. */
  private sendChunk(from: number, to: number): Promise<number> {
    return new Promise((ok, fail) => {
      const xhr = new XMLHttpRequest();
      this.xhr = xhr;
      xhr.open('PATCH', `/api/v1/portal/uploads/${this.id}`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('content-type', 'application/offset+octet-stream');
      xhr.setRequestHeader('upload-offset', String(from));
      xhr.upload.onprogress = (e) => this.progress(from + e.loaded);
      xhr.onload = () => {
        let body: { receivedBytes?: number; error?: { code?: string; message?: string; details?: Record<string, unknown> } } = {};
        try { body = JSON.parse(xhr.responseText || '{}'); } catch { /* not JSON */ }
        if (xhr.status === 200 && typeof body.receivedBytes === 'number') { this.progress(body.receivedBytes); ok(body.receivedBytes); return; }
        if (xhr.status === 401) window.dispatchEvent(new CustomEvent('portal:signed-out'));
        fail(new PortalApiError(xhr.status, body.error?.code ?? 'ERROR', body.error?.message ?? xhr.statusText, body.error?.details));
      };
      xhr.onerror = () => fail(new PortalApiError(0, 'NETWORK', 'The connection was lost'));
      xhr.onabort = () => fail(new PortalApiError(0, 'ABORTED', 'Cancelled'));
      xhr.send(this.file.slice(from, to));
    });
  }

  private progress(at: number) {
    const now = performance.now();
    if (!this.lastTick.at) this.lastTick = { at: now, bytes: at };
    const dt = (now - this.lastTick.at) / 1000;
    if (dt >= 0.5) {
      const rate = Math.max(0, at - this.lastTick.bytes) / dt;
      this.speed = this.speed ? this.speed * 0.6 + rate * 0.4 : rate;
      this.lastTick = { at: now, bytes: at };
    }
    this.sent = Math.max(this.sent, Math.min(at, this.file.size));
    this.emit();
  }

  /** Stops and aborts the upload on the server. */
  async cancel(): Promise<void> {
    this.set('cancelled');
    this.xhr?.abort();
    dropPending(this.key);
    if (this.id) await portalApi(`/uploads/${this.id}`, { method: 'DELETE' }).catch(() => undefined);
  }
}

/** The uploads of this page session, by key (an item's, or an open upload's). */
const registry = new Map<string, PortalUpload>();
const registryListeners = new Set<() => void>();
export const uploadFor = (key: string) => registry.get(key) ?? null;
export function subscribeUploads(fn: () => void): () => void { registryListeners.add(fn); return () => registryListeners.delete(fn); }

/** Starts (or, with resumeId, continues) an upload; `onDone` runs once it is complete. */
export function startUpload(key: string, file: File, target: UploadTarget, opts: { resumeId?: string | null; onDone?: (r: UploadResult) => void } = {}): PortalUpload {
  const u = new PortalUpload(key, file, target, opts.resumeId ?? null);
  registry.set(key, u);
  for (const l of registryListeners) l();
  void u.run().then((r) => { if (r) opts.onDone?.(r); for (const l of registryListeners) l(); });
  return u;
}
export const anyUploadActive = () => [...registry.values()].some((u) => u.active);
